import './index.css'
import cn from 'clsx'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import type { EmailCampaign } from 'types/campaign'
import type { PlaylistProps } from 'types/content-editor'
import type { Playlist, PlaylistId } from 'types/playlist'
import { getPlaylistForCampaign } from '@disco/state-management/actions/campaign-details-actions'
import { getCampaign } from '@disco/state-management/selectors/campaigns'
import { getTrackSubheader } from '@disco/utils/tracks'
import TrackArtwork from 'components/common/tracks/track-artwork'
import { Spinner } from 'components/ui'
import type { Track } from 'types/track'
const defaultStyle: Record<string, any> = {
  color: '#fff',
  backgroundColor: '#0693e3',
}

const getTrack = name => ({
  name,
  album: 'Album',
  artist: 'Artist',
  ext: '',
})

const playlistTemplate = {
  name: 'Playlist',
  tracks: [getTrack('Track #1'), getTrack('Track #2'), getTrack('Track #3')],
  view_playlist_url: null,
  sections: [],
}

const PlaylistBlock = ({
  buttonAlignment,
  buttonColor,
  buttonBackgroundColor,
  buttonText,
  disableHeader,
  campaign: { id: campaignId },
  playlist_id,
}: PlaylistProps & {
  editMode?: boolean
  sectionIndex: number
  columnIndex: number
  contentIndex: number
  playlist_id: PlaylistId
  campaign: EmailCampaign
}): React.ReactElement<'div'> => {
  const campaign = useSelector(state => {
    return getCampaign(state, {
      campaignId,
    })
  })
  const dispatch = useDispatch()
  const [playlist, setPlaylist] = useState(
    campaign && campaign.playlists && campaign.playlists[playlist_id],
  )
  const [error, setError] = useState(null)
  useEffect(() => {
    const existingPlaylist = campaign?.playlists?.[playlist_id]

    if (!existingPlaylist && playlist_id) {
      dispatch(getPlaylistForCampaign(campaignId, String(playlist_id)))
        .then(({ updatedFields: { playlists } }) => {
          setPlaylist(playlists[playlist_id])
          setError(null)
        })
        .catch(err => {
          setError(err)
        })
    } else {
      setPlaylist(existingPlaylist)
      setError(null)
    }
  }, [playlist_id, campaign, campaignId, dispatch])
  const p = playlist || playlistTemplate
  let buttonStyle = { ...defaultStyle }
  if (buttonColor) buttonStyle.color = buttonColor
  if (buttonBackgroundColor) buttonStyle.backgroundColor = buttonBackgroundColor

  if (playlist_id && playlist_id !== playlist?.playlist_id && !error) {
    return (
      <div className="content-editor__playlist-block__indicator">
        <Spinner
          style={{
            margin: '2rem auto',
          }}
        />
        <div className="content-editor__playlist-block__indicator__caption">
          Loading playlist ...
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="content-editor__playlist-block">
        <Spinner
          style={{
            margin: '2rem auto',
          }}
          paused
          red
        />
        <div className="content-editor__playlist-block__indicator__caption">
          {error.toString()}
        </div>
      </div>
    )
  }

  return (
    <div className="content-editor__playlist-block">
      {!disableHeader && (
        <div className="content-editor__playlist-block__title">{p.name}</div>
      )}
      <div className="content-editor__playlist-block__tracklist">
        {p.tracks.map((track, index) => {
          const section = p.sections.find(s => s.position === index)
          const sectionBlock = section ? (
            <div className="content-editor__playlist-block__tracklist__section">
              {section.name}
            </div>
          ) : null
          return (
            <React.Fragment key={index}>
              {sectionBlock}
              <div className="content-editor__playlist-block__tracklist__item">
                <div className="content-editor__playlist-block__tracklist__item__artwork">
                  <TrackArtwork track={track as Track} />
                </div>
                <div className="content-editor__playlist-block__tracklist__item__details">
                  <div className="content-editor__playlist-block__tracklist__item__name">
                    {track.name}
                  </div>
                  <div className="content-editor__playlist-block__tracklist__item__subheader">
                    {getTrackSubheader(track)}
                  </div>
                </div>
              </div>
            </React.Fragment>
          )
        })}
      </div>
      <div
        className={cn(
          'content-editor__playlist-block__action-button',
          `content-editor__playlist-block__action-button--${
            buttonAlignment || 'left'
          }`,
        )}
      >
        <a
          className="content-editor__playlist__button"
          style={buttonStyle}
          href={p.view_playlist_url || ''}
          rel="noopener noreferrer"
          target="_blank"
        >
          {buttonText}
        </a>
      </div>
    </div>
  )
}

export default PlaylistBlock
